import type { Design } from "./design";
import { applyDesignChanges, type DesignChanges } from "./design-sync";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues?: Array<{ path: string; code: string; message: string }>,
  ) {
    super(message);
  }
}
export async function api<T>(
  path: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const init = {
    signal,
    method,
    headers: { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  };
  let res: Response;
  try {
    res = await fetch(`/api${path}`, init);
  } catch {
    throw new ApiError(
      0,
      "Cannot reach Proxima Forma. Check the connection and try again.",
    );
  }
  if (res.status === 204) return undefined as T;
  let data: unknown;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(
      res.status,
      "The server could not complete this request. Please try again.",
    );
  }
  if (!res.ok) {
    const details = data && typeof data === "object" && "details" in data && Array.isArray(data.details) ? data.details : [];
    throw new ApiError(
      res.status,
      data &&
        typeof data === "object" &&
        "error" in data &&
        typeof data.error === "string"
        ? data.error
        : "The request could not be completed.",
      details.slice(0, 8).map(issue => ({ path: Array.isArray(issue.path) ? issue.path.join(".") : String(issue.path || "input"), code: String(issue.code || "invalid"), message: String(issue.message || "Invalid field") })),
    );
  }
  return data as T;
}
export async function download(path: string, filename: string) {
  const res = await fetch(path);
  if (!res.ok) throw Error("Export failed. Please try again.");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function syncDesign(projectId: string, current: Design | null, signal?: AbortSignal): Promise<Design> {
  const path = `/projects/${encodeURIComponent(projectId)}`;
  if (!current || current.id !== projectId) return api<Design>(path, "GET", undefined, signal);
  const changes = await api<DesignChanges | undefined>(`${path}/changes?revision=${current.revision}`, "GET", undefined, signal);
  return changes ? applyDesignChanges(current, changes) : current;
}
